import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ROLES } from '../../../core/helpers/constants';
import { createGuid } from '../../../core/helpers/utility';
import { IClient } from '../../../core/models/clients.models';
import { ICompany, ISalesRep, IUser } from '../../../core/models/datafeed.models';
import { ClientsApiService } from '../../../pages/clients/clients.apiservice';
import { AppService } from '../../../store/app.service';
import { DataFeedService } from '../../api/datafeed.service';
import { InstanceService } from '../../../core/instance/instance.service';

@Component({
  selector: 'modal-add-client',
  templateUrl: './add-client.component.html',
  styleUrls: ['./add-client.component.scss']
})
export class AddClientModal implements OnInit, OnDestroy {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  @Output() oSaveClicked = new EventEmitter<any>();

  allCompanies: Array<ICompany> = new Array<ICompany>();
  allSalesReps: Array<ISalesRep> = new Array<ISalesRep>();
  allDms: Array<ISalesRep> = new Array<ISalesRep>();
  allEmployees: Array<IUser> = new Array<IUser>();
  allSubEmployees: Array<IUser> = new Array<IUser>();
  allClientOwners: Array<IUser> = new Array<IUser>();
  client: any;
  addClientForm!: FormGroup;
  submitted = false;
  isProspect = false;

  @Input() set iId(value: number | null) {
    this.id = value;
    if (value !== null) {
      this.clientApiService.getClientDetails(value)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response: any) => {
          this.client = response.data;
          this.addClientForm.patchValue(response.data);
          // Ensure isProspect is always a real boolean when editing.
          // Some records may come back null/undefined, and the ui-switch can treat that as checked.
          const normalizedIsProspect = response.data?.isProspect === true;
          this.addClientForm.patchValue({ isProspect: normalizedIsProspect }, { emitEvent: false });
          this.isProspect = normalizedIsProspect;
          this.addClientForm.patchValue({ employees: response.data.employees.map((x: any) => ({ id: x.id, fullName: `${x.firstName} ${x.lastName}` })) });

          // CPJP alt layout uses lastName as the displayed name; force firstName to a placeholder.
          if (this.instance.isAltLayout) {
            this.addClientForm.patchValue({ firstName: '--' }, { emitEvent: false });
          }
        });
    }
  }
  id: number | null = null;

  constructor(
    private dataFeedService: DataFeedService,
    private clientApiService: ClientsApiService,
    private appService: AppService,
    public activeModal: NgbActiveModal,
    private formBuilder: FormBuilder,
    public instance: InstanceService) {
    this.addClientForm = this.formBuilder.group({
      id: [null],
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      brandAddress: [''],
      brandId: [null],
      dmsId: [null],
      salesRepId: [null],
      clientOwnerId: [null],
      employees: [null],
      subEmployeeIds: [''],
      isProspect: [false],
      isSendInvoice: [true],
      emailForInvoice: [''],
      phone: [''],
      email: ['', [Validators.required, Validators.email]],
    });
  }
  get f() { return this.addClientForm.controls; }

  updateData(data: IClient) {
    this.addClientForm.patchValue(data);
  }

  private loadLookupData() {
    this.dataFeedService.getAllCompanies()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        if (response.good) {
          this.allCompanies = [...response.data];
        }
      });

    this.dataFeedService.getAllUsersWithRole(ROLES.SalesRep.Code)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        if (response.good) {
          this.allSalesReps = [...response.data];
        }
      });

    this.dataFeedService.getAllUsersWithRole(ROLES.DMS.Code)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        if (response.good) {
          this.allDms = [...response.data];
        }
      });

    this.dataFeedService.getAllUsersWithRole(ROLES.ClientOwner.Code)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        if (response.good) {
          this.allClientOwners = [...response.data];
        }
      });
  }

  ngOnInit() {
    if (this.instance.isAltLayout) {
      this.addClientForm.patchValue({ firstName: '--' }, { emitEvent: false });
    }

    // Defer lookup loading to avoid ExpressionChangedAfterItHasBeenCheckedError
    // when ngx-ui-loader flips state during the modal open change-detection tick.
    Promise.resolve().then(() => this.loadLookupData());

    this.appService.getActiveRoute$()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((route: any) => {
        if (route.url.indexOf('prospect') > -1 || route.url.indexOf('prospect-details') > -1 || route.url.indexOf('archived-locations') > -1 || route.url.indexOf('archived-location-details') > -1) {
          if (this.id === null)
          {
            this.addClientForm.patchValue({ isProspect: true });
            this.isProspect = true;
          }
        } 
      });
  }
  onSubmit() {
    this.submitted = true;
    if (this.addClientForm.invalid)
      return;
    if (this.f.id.value === null)
      this.f.id.setValue(createGuid())
    
    let request = this.addClientForm.value;
    request = this.trimObjectStrings(request); 
    this.oSaveClicked.emit(request);
  }

  // This function will recursively iterate through the object
  trimObjectStrings(obj: any): any {
    // Iterate over each property of the object
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        
        if (typeof value === 'string') {
          // Trim the string if the value is a string
          obj[key] = value.trim();
        } else if (typeof value === 'object' && value !== null) {
          // Recursively call the function if the value is an object (or array)
          this.trimObjectStrings(value);
        }
      }
    }
    return obj;
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

}
